import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, unref as _unref, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "bg-white border border-dashboard-border rounded-xl shadow-sm p-4 mb-6" }
const _hoisted_2 = { class: "flex flex-col lg:flex-row lg:items-end gap-3" }
const _hoisted_3 = { class: "flex-1" }
const _hoisted_4 = { class: "flex items-center gap-2 mb-2" }
const _hoisted_5 = {
  key: 0,
  class: "flex items-center gap-2"
}
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { class: "grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2 flex-[2]" }
const _hoisted_8 = ["value"]
const _hoisted_9 = ["value"]

import { ref, computed, watch, onMounted } from 'vue'
import { X } from 'lucide-vue-next'
import { apiFetch } from '@/utils/apiFetch'
import MultiSelectDropdown from './MultiSelectDropdown.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'StatsFilterBar',
  props: {
    ambassadeDefaultMode: { default: 'include' },
    directionDefaultMode: { default: 'exclude' }
  },
  emits: ["change"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const STAGES = [
  { value: 'en_attente', label: 'En attente' },
  { value: 'envoye_sg', label: 'Envoyé au SG' },
  { value: 'chez_ambassadeur', label: "Chez l'ambassadeur" },
  { value: 'affecte', label: 'Affecté' },
  { value: 'en_traitement', label: 'En traitement' },
  { value: 'valide', label: 'Validé' },
  { value: 'en_attente_archivage', label: "En attente d'archivage" },
  { value: 'archive', label: 'Archivé' },
]

// Par défaut : 30 derniers jours
const today = new Date()
const monthAgo = new Date(today.getTime() - 30 * 24 * 3600 * 1000)
const iso = (d: Date) => d.toISOString().slice(0, 10)

const periodMode = ref<'range' | 'date'>('range')
const from = ref(iso(monthAgo))
const to = ref(iso(today))
const date = ref(iso(today))
const ambassade = ref<string[]>([])
const direction = ref<string[]>([])
const ambassadeMode = ref(props.ambassadeDefaultMode)
const directionMode = ref(props.directionDefaultMode)
const role = ref<string[]>([])
const roleMode = ref('include')
const user = ref('')
const stage = ref('')

// Rôles « cabinet » (haut de la hiérarchie) qui polluent le global.
const CABINET_ROLES = ['MINISTRE', 'DC', 'SG', 'SGA', 'ASSISTANT_SGA', 'SP_MINISTRE', 'SP_DC', 'SP_SG']

const ambassades = ref<any[]>([])
const directions = ref<any[]>([])
const roles = ref<any[]>([])
const users = ref<any[]>([])

const ambassadeOptions = computed(() =>
  ambassades.value.map((a) => ({ value: String(a.id), label: a.nom })))
const directionOptions = computed(() =>
  directions.value.map((d) => ({ value: String(d.id), label: d.nom })))
const roleOptions = computed(() =>
  roles.value.map((r) => ({ value: String(r.code), label: r.nom || r.code })))

const userLabel = (u: any) =>
  (`${u.first_name || ''} ${u.last_name || ''}`.trim()) || u.full_name || u.username || `#${u.id}`

function excludeCabinet() {
  const codes = new Set(roles.value.map((r) => String(r.code)))
  role.value = CABINET_ROLES.filter((c) => codes.size === 0 || codes.has(c))
  roleMode.value = 'exclude'
}

const hasActiveFilters = computed(() =>
  Boolean(ambassade.value.length || direction.value.length || role.value.length || user.value || stage.value),
)

function buildParams(): Record<string, string> {
  const p: Record<string, string> = {}
  if (periodMode.value === 'date') {
    if (date.value) p.date = date.value
  } else {
    if (from.value) p.from = from.value
    if (to.value) p.to = to.value
  }
  if (ambassade.value.length) {
    p.ambassade = ambassade.value.join(',')
    p.ambassade_mode = ambassadeMode.value
  }
  if (direction.value.length) {
    p.direction = direction.value.join(',')
    p.direction_mode = directionMode.value
  }
  if (role.value.length) {
    p.role = role.value.join(',')
    p.role_mode = roleMode.value
  }
  if (user.value) p.user = user.value
  if (stage.value) p.stage = stage.value
  return p
}

let debounce: ReturnType<typeof setTimeout> | undefined
function emitChange() {
  if (debounce) clearTimeout(debounce)
  debounce = setTimeout(() => emit('change', buildParams()), 250)
}

watch(
  [periodMode, from, to, date, ambassade, direction, ambassadeMode, directionMode,
   role, roleMode, user, stage],
  emitChange,
  { deep: true },
)

function reset() {
  ambassade.value = []
  direction.value = []
  ambassadeMode.value = props.ambassadeDefaultMode
  directionMode.value = props.directionDefaultMode
  role.value = []
  roleMode.value = 'include'
  user.value = ''
  stage.value = ''
}

async function loadOptions() {
  const safe = async (path: string) => {
    try {
      const res = await apiFetch(path)
      return Array.isArray(res) ? res : (res?.data || [])
    } catch {
      return []
    }
  }
  ;[directions.value, ambassades.value, roles.value, users.value] = await Promise.all([
    safe('/api/admin/directions/'),
    safe('/api/admin/ambassades/'),
    safe('/api/admin/roles/'),
    safe('/api/admin/users/'),
  ])
}

onMounted(() => {
  loadOptions()
  emit('change', buildParams())
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[14] || (_cache[14] = _createElementVNode("label", { class: "block text-xs font-medium text-dashboard-muted mb-1" }, "Période", -1)),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("button", {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (periodMode.value = 'range')),
            class: _normalizeClass(['px-3 py-1 rounded-md text-xs font-medium border transition-colors',
                     periodMode.value === 'range' ? 'bg-[#22282F] text-white border-[#22282F]' : 'bg-white text-dashboard-text border-dashboard-border'])
          }, "Intervalle", 2),
          _createElementVNode("button", {
            onClick: _cache[1] || (_cache[1] = ($event: any) => (periodMode.value = 'date')),
            class: _normalizeClass(['px-3 py-1 rounded-md text-xs font-medium border transition-colors',
                     periodMode.value === 'date' ? 'bg-[#22282F] text-white border-[#22282F]' : 'bg-white text-dashboard-text border-dashboard-border'])
          }, "Date fixe", 2)
        ]),
        (periodMode.value === 'range')
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((from).value = $event)),
                type: "date",
                class: "px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm"
              }, null, 512), [
                [_vModelText, from.value]
              ]),
              _cache[13] || (_cache[13] = _createElementVNode("span", { class: "text-dashboard-muted text-sm" }, "→", -1)),
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((to).value = $event)),
                type: "date",
                class: "px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm"
              }, null, 512), [
                [_vModelText, to.value]
              ])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((date).value = $event)),
                type: "date",
                class: "px-3 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm"
              }, null, 512), [
                [_vModelText, date.value]
              ])
            ]))
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("div", null, [
          _cache[15] || (_cache[15] = _createElementVNode("label", { class: "block text-xs font-medium text-dashboard-muted mb-1" }, "Ambassade(s)", -1)),
          _createVNode(MultiSelectDropdown, {
            label: "Toutes",
            options: ambassadeOptions.value,
            modelValue: ambassade.value,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((ambassade).value = $event)),
            mode: ambassadeMode.value,
            "onUpdate:mode": _cache[6] || (_cache[6] = ($event: any) => (ambassadeMode.value = $event)),
            "show-mode": ""
          }, null, 8, ["options", "modelValue", "mode"])
        ]),
        _createElementVNode("div", null, [
          _cache[16] || (_cache[16] = _createElementVNode("label", { class: "block text-xs font-medium text-dashboard-muted mb-1" }, "Direction(s)", -1)),
          _createVNode(MultiSelectDropdown, {
            label: "Toutes",
            options: directionOptions.value,
            modelValue: direction.value,
            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((direction).value = $event)),
            mode: directionMode.value,
            "onUpdate:mode": _cache[8] || (_cache[8] = ($event: any) => (directionMode.value = $event)),
            "show-mode": ""
          }, null, 8, ["options", "modelValue", "mode"])
        ]),
        _createElementVNode("div", null, [
          _cache[17] || (_cache[17] = _createElementVNode("label", { class: "block text-xs font-medium text-dashboard-muted mb-1" }, "Rôle(s) — détenteur", -1)),
          _createVNode(MultiSelectDropdown, {
            label: "Tous",
            options: roleOptions.value,
            modelValue: role.value,
            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((role).value = $event)),
            mode: roleMode.value,
            "onUpdate:mode": _cache[10] || (_cache[10] = ($event: any) => (roleMode.value = $event)),
            "show-mode": ""
          }, null, 8, ["options", "modelValue", "mode"]),
          _createElementVNode("button", {
            type: "button",
            onClick: excludeCabinet,
            class: "mt-1 text-[11px] text-red-600 hover:underline",
            title: "Exclure Ministre, DC, SG, SGA, Assistant SGA, SP…"
          }, "Exclure le cabinet")
        ]),
        _createElementVNode("div", null, [
          _cache[19] || (_cache[19] = _createElementVNode("label", { class: "block text-xs font-medium text-dashboard-muted mb-1" }, "Agent", -1)),
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((user).value = $event)),
            class: "w-full px-2 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm"
          }, [
            _cache[18] || (_cache[18] = _createElementVNode("option", { value: "" }, "Tous", -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(users.value, (u) => {
              return (_openBlock(), _createElementBlock("option", {
                key: u.id,
                value: String(u.id)
              }, _toDisplayString(userLabel(u)), 9, _hoisted_8))
            }), 128))
          ], 512), [
            [_vModelSelect, user.value]
          ])
        ]),
        _createElementVNode("div", null, [
          _cache[21] || (_cache[21] = _createElementVNode("label", { class: "block text-xs font-medium text-dashboard-muted mb-1" }, "Étape", -1)),
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((stage).value = $event)),
            class: "w-full px-2 py-2 bg-dashboard-bg border border-dashboard-border rounded-lg text-sm"
          }, [
            _cache[20] || (_cache[20] = _createElementVNode("option", { value: "" }, "Toutes", -1)),
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(STAGES, (s) => {
              return _createElementVNode("option", {
                key: s.value,
                value: s.value
              }, _toDisplayString(s.label), 9, _hoisted_9)
            }), 64))
          ], 512), [
            [_vModelSelect, stage.value]
          ])
        ])
      ]),
      _createElementVNode("div", null, [
        (hasActiveFilters.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              onClick: reset,
              class: "px-3 py-2 text-dashboard-muted hover:text-dashboard-text text-sm flex items-center gap-1 border border-dashed border-dashboard-border rounded-lg hover:bg-dashboard-bg"
            }, [
              _createVNode(_unref(X), { class: "w-4 h-4" }),
              _cache[22] || (_cache[22] = _createTextVNode(" Réinitialiser "))
            ]))
          : _createCommentVNode("", true)
      ])
    ])
  ]))
}
}

})